import { isoDateValue } from './trade-invoice-dates';
import { withReclassification } from './trade-invoice-reclassify';
import { toWireSchedule } from './final-settlement-sell-payload';
import type { InvoiceRow } from '../types';
import type { ProvisionalSubmitValues } from './provisional-invoice-schema';
import type { FinalSettlementSubmitValues } from './final-settlement-invoice-schema';

export interface SaveContext {
  readonly side: 'buy' | 'sell';
  readonly orderId: string;
  readonly blOrderId: string;
  readonly existingInvoice: InvoiceRow | null;
  readonly aiExtractionId?: string;
}

interface BaseWirePayload extends Record<string, unknown> {
  invoice_number: string;
  currency: 'USD' | 'EUR';
  total_amount: number;
  vat_amount: number;
  original_due_date: string;
  issue_date?: string;
  market_reference_price: number;
  market_reference_date: string;
  amount_qt_mt: number;
  applied_downpayment_amount: number;
  company_name?: string;
  issuer_company_name?: string;
  item_description?: string;
  payment_term?: string;
  remarks?: string;
  payment_account_key?: string | null;
}

function baseWirePayload(values: ProvisionalSubmitValues, side: 'buy' | 'sell'): BaseWirePayload {
  const out: BaseWirePayload = {
    invoice_number: values.invoice_number,
    currency: values.currency,
    total_amount: values.total_amount,
    vat_amount: values.vat_amount,
    original_due_date: isoDateValue(values.original_due_date),
    market_reference_price: values.market_reference_price,
    market_reference_date: isoDateValue(values.market_reference_date),
    amount_qt_mt: values.amount_qt_mt,
    applied_downpayment_amount: values.applied_downpayment_amount,
  };
  if (values.issue_date !== undefined) out.issue_date = values.issue_date;
  if (values.company_name !== undefined) out.company_name = values.company_name;
  if (side === 'buy' && values.issuer_company_name !== undefined) {
    out.issuer_company_name = values.issuer_company_name;
  }
  if (values.item_description !== undefined) out.item_description = values.item_description;
  if (values.payment_term !== undefined) out.payment_term = values.payment_term;
  if (values.remarks !== undefined) out.remarks = values.remarks;
  if (values.payment_account_key !== undefined) {
    // 'auto' = no override; the backend routes by currency/country.
    out.payment_account_key =
      values.payment_account_key === 'auto' ? null : values.payment_account_key;
  }
  return out;
}

export function buildProvisionalSavePayload(
  values: ProvisionalSubmitValues,
  ctx: SaveContext,
): Record<string, unknown> {
  const base = baseWirePayload(values, ctx.side);
  if (ctx.existingInvoice !== null) return withReclassification(base, values, 'Provisional');
  const out: Record<string, unknown> = {
    bl_order_id: ctx.blOrderId,
    order_id: ctx.orderId,
    invoice_direction: ctx.side === 'buy' ? 'Purchase' : 'Sale',
    invoice_type: 'Provisional',
    ...base,
  };
  if (ctx.aiExtractionId !== undefined) out['ai_extraction_id'] = ctx.aiExtractionId;
  return out;
}

export function buildFinalSettlementSavePayload(
  values: FinalSettlementSubmitValues,
  ctx: SaveContext,
): Record<string, unknown> {
  const base = baseWirePayload(values, ctx.side);
  const withApplied: Record<string, unknown> = {
    ...base,
    applied_provisional_amount: values.applied_provisional_amount,
  };
  if (ctx.existingInvoice !== null) {
    return withReclassification(withApplied, values, 'Final Settlement');
  }
  const out: Record<string, unknown> = {
    bl_order_id: ctx.blOrderId,
    order_id: ctx.orderId,
    invoice_direction: ctx.side === 'buy' ? 'Purchase' : 'Sale',
    invoice_type: 'Final Settlement',
    ...withApplied,
  };
  if (ctx.aiExtractionId !== undefined) out['ai_extraction_id'] = ctx.aiExtractionId;
  const schedule = toWireSchedule(values.payment_schedule);
  if (ctx.side === 'sell' && schedule.length > 0) out['payment_schedule'] = schedule;
  return out;
}
